import {
  LEVEL_ZERO_SKIN,
  compactXp,
  levelBenefits,
  levelLabel,
  levelTiers,
  tierIndexOfLevel,
  tierXpBounds,
  unrankedStep,
  xpForLevelIn,
  type LevelBenefit,
  type LevelTier,
} from '@/utils/levels';
import { getAssetsUrlForCDN } from '@/utils/template';

/** Onde o elo em foco está em relação ao elo do jogador. */
export type TierState = 'done' | 'current' | 'locked';

export interface TierBenefit extends LevelBenefit {
  /** Ainda não liberado: exige nível acima do teto do elo em foco. */
  isLocked: boolean;
  /** Liberado exatamente neste elo — é o que o elo tem de novo. */
  isNew: boolean;
}

/**
 * Traduz o progresso do jogador na Smartico para a tela do Clube Fidelidade.
 *
 * A Smartico é a fonte da verdade de tudo que é do jogador: XP acumulado
 * (`ach_points_ever`), número do nível, o nível atual e o próximo, e a
 * porcentagem da barra — a store já calcula isso a partir de `getLevels()`.
 * A tabela local (`LEVEL_XP`) entra só como fallback, para a página ainda
 * renderizar enquanto a régua da Smartico não chegou. Quem faz essa leitura é
 * `useSmarticoLevel`; aqui em cima dela vem o mapa de elos.
 *
 * O que é sempre local é o mapa de elos e benefícios: a Smartico não tem
 * noção de "Bronze"/"Diamante" nem de quais benefícios cada faixa abre — isso
 * é dado de design, não do jogador.
 *
 * Chame uma vez por tela e passe o resultado adiante por props — cada chamada
 * cria seu próprio estado de navegação.
 */
export function useLevelsProgress() {
  const {
    currentUserProfileAvatar,
    currentUserLevelData,
    nextUserLevel,
    progressToAnotherLevel,
  } = storeToRefs(useSmarticoUserInfos());

  // Nível, XP e régua saem de `useSmarticoLevel` — a celebração de subida de elo
  // lê os mesmos números de lá, e é o que impede as duas leituras de divergirem.
  const { xp, level, hasSmarticoLevels, levelXpTable, maxLevel } =
    useSmarticoLevel();

  /**
   * Topo da escala. Cuidado: `nextUserLevel` ausente NÃO prova que o jogador
   * chegou ao fim — a store tem um segundo caminho (`loadCurrentLevelData`,
   * usado quando `getLevels()` não responde) que preenche o nível atual e nunca
   * o próximo. Antes isso fazia um jogador do meio da escala aparecer como
   * "nível máximo". Só declaramos topo quando a régua confirma.
   */
  const isMaxLevel = computed(() => {
    if (nextUserLevel.value) return false;

    return level.value >= maxLevel.value;
  });

  /** XP de entrada do nível atual — o piso da barra. */
  const levelFloorXp = computed(() => {
    if (currentUserLevelData.value) {
      return currentUserLevelData.value.required_points;
    }

    // O índice É o nível, então o piso do nível atual é o próprio `[level]`.
    // Quem começa do zero é o nível 0 — o nível 1 já custa os 200 XP dele.
    return xpForLevelIn(levelXpTable.value, level.value);
  });

  /** XP que fecha o nível atual — o teto da barra. */
  const nextLevelXp = computed(() => {
    // Melhor caso: a store já resolveu qual é o próximo nível.
    if (nextUserLevel.value) return nextUserLevel.value.required_points;

    if (isMaxLevel.value) {
      return xpForLevelIn(levelXpTable.value, maxLevel.value);
    }

    // Sem `nextUserLevel`: o teto é o XP do nível seguinte na régua.
    return xpForLevelIn(levelXpTable.value, level.value + 1);
  });

  const nextLevel = computed(() => Math.min(level.value + 1, maxLevel.value));

  /**
   * Progresso no nível atual. Com a Smartico presente é o número que ela dá,
   * sem piso artificial: se está em 0%, a barra fica vazia — é o valor real.
   */
  const levelProgress = computed(() => {
    if (hasSmarticoLevels.value) return progressToAnotherLevel.value;

    if (isMaxLevel.value) return 100;

    const span = nextLevelXp.value - levelFloorXp.value;

    if (span <= 0) return 100;

    return Math.max(
      0,
      Math.min(100, ((xp.value - levelFloorXp.value) / span) * 100),
    );
  });

  const xpToNextLevel = computed(() =>
    Math.max(0, nextLevelXp.value - xp.value),
  );

  /**
   * O par que o contador da faixa de nível mostra, medido DENTRO do nível atual:
   * quanto o jogador andou desde a entrada dele e quanto o nível custa por
   * inteiro. É a mesma régua da barra — o acumulado da vida (`xp`) contra o teto
   * absoluto do próximo nível daria números que não conversam com a
   * porcentagem ao lado (nível 20 aparecia como "15938387859 / 200000000",
   * enquanto a barra dizia 60%).
   *
   * `Math.max(0, ...)` porque o piso vem da Smartico e o acumulado também: numa
   * janela em que os dois se atualizam fora de sincronia (subida de nível), a
   * subtração pode ficar negativa por um instante.
   */
  const levelXp = computed(() => Math.max(0, xp.value - levelFloorXp.value));

  /** O tamanho do nível atual — o denominador do par acima. */
  const levelXpSpan = computed(() =>
    Math.max(0, nextLevelXp.value - levelFloorXp.value),
  );

  /**
   * O número do nível é o da Smartico; a formatação ("Nível 18" / "VIP 3") é
   * nossa, do design. Não usamos o `name` que vem da Smartico porque lá ele
   * chega como "bronze LV 3" — não cabe num título de 30px.
   */
  const currentLevelLabel = computed(() => levelLabel(level.value));
  const nextLevelLabel = computed(() => levelLabel(nextLevel.value));

  /**
   * O nome sai de `useSmarticoNickname`: apelido da Smartico na frente, nome do
   * cadastro só como fallback. É a MESMA leitura do header e do dropdown, então
   * a placa e o menu falam do jogador do mesmo jeito — e `openChangeNickname`
   * vem junto porque toda tela que mostra o nome também oferece o lápis.
   */
  const { displayName: playerName, openChangeNickname } = useSmarticoNickname();

  const playerAvatar = computed(() => currentUserProfileAvatar.value);

  /* ---------------- elo do jogador ---------------- */

  /** Elos com as artes resolvidas pelo CDN (só dá pra fazer aqui, no contexto Nuxt). */
  const tiers = computed(() => levelTiers());

  const tierIndex = computed(() => tierIndexOfLevel(level.value));

  /**
   * Nível 0 = "sem elo": a conta nasceu mas não conquistou nada ainda. O
   * jogador não veste Bronze (fica cinza, com o emblema rachado) e não acende
   * o "você" em grid nenhum — para isso existe o `highlightTierIndex`.
   */
  const isUnranked = computed(() => level.value === 0);

  const tier = computed<LevelTier>(() => {
    const base = tiers.value[tierIndex.value];

    if (!isUnranked.value) return base;

    return {
      ...base,
      name: 'Sem elo',
      color: LEVEL_ZERO_SKIN.color,
      accent: LEVEL_ZERO_SKIN.accent,
      badge: getAssetsUrlForCDN(LEVEL_ZERO_SKIN.badge),
    };
  });

  /**
   * A fila do swiper de progresso: o degrau "Sem elo" (nível 0, arte rachada)
   * na frente e os 14 elos atrás. TODA a navegação (viewIndex, prev/next,
   * estado) roda sobre esta fila — quem fala em índice de ELO (carrossel,
   * comparativo, trilha) converte com o +1/-1 dos helpers abaixo.
   */
  const viewSteps = computed<LevelTier[]>(() => [
    unrankedStep(),
    ...tiers.value,
  ]);

  /** Posição do jogador na fila de degraus (0 = Sem elo). */
  const playerStepIndex = computed(() =>
    isUnranked.value ? 0 : tierIndex.value + 1,
  );

  /**
   * Índice do elo do jogador para efeito de DESTAQUE (coluna "você" do
   * comparativo, cartão aceso do carrossel): no nível 0 é -1 — nenhum elo é
   * dele ainda, então nada acende.
   */
  const highlightTierIndex = computed(() =>
    isUnranked.value ? -1 : tierIndex.value,
  );

  /* ---------------- navegação do swiper de degraus ---------------- */

  /** `null` = seguindo o degrau do jogador; number = degrau fixado. */
  const pinnedIndex = ref<number | null>(null);

  const viewIndex = computed(() => pinnedIndex.value ?? playerStepIndex.value);
  const viewTier = computed<LevelTier>(() => viewSteps.value[viewIndex.value]);

  /**
   * Índice de ELO do degrau em foco: -1 quando o foco está no "Sem elo".
   * É o que o carrossel dos 14 cartões usa pra acender a seleção.
   */
  const viewTierIndex = computed(() => viewIndex.value - 1);

  const prevTier = computed<LevelTier | null>(
    () => viewSteps.value[viewIndex.value - 1] ?? null,
  );

  const nextTier = computed<LevelTier | null>(
    () => viewSteps.value[viewIndex.value + 1] ?? null,
  );

  const viewState = computed<TierState>(() => {
    if (viewIndex.value < playerStepIndex.value) return 'done';

    return viewIndex.value === playerStepIndex.value ? 'current' : 'locked';
  });

  /** Navega por índice de DEGRAU (0 = Sem elo, 1 = Bronze…). */
  function goToStep(index: number) {
    pinnedIndex.value = Math.max(
      0,
      Math.min(viewSteps.value.length - 1, index),
    );
  }

  /** Navega por índice de ELO (0 = Bronze…) — para grids que não veem o degrau 0. */
  function goToTier(index: number) {
    goToStep(index + 1);
  }

  function stepTier(direction: number) {
    const target = viewIndex.value + direction;

    if (target < 0 || target > viewSteps.value.length - 1) return;

    goToStep(target);
  }

  /* ---------------- benefícios do elo em foco ---------------- */

  /** XP em que o jogador entra e sai do elo em foco. */
  const viewBounds = computed(() =>
    tierXpBounds(levelXpTable.value, viewTier.value),
  );

  /**
   * Todos os benefícios na ordem do comparativo — que é a fonte da verdade da
   * tela. A lista não reagrupa por liberado/bloqueado: o jogador acha a mesma
   * linha na mesma posição na matriz e aqui.
   */
  const viewBenefits = computed<TierBenefit[]>(() => {
    const { from, to } = viewTier.value;

    return levelBenefits().map((benefit) => {
      // Benefício pendente (`pending`, ex.: saldo bônus) fica de cadeado em
      // TODOS os elos — a operação ainda não o entrega.
      const isNew =
        !benefit.pending && benefit.from >= from && benefit.from <= to;

      const isLocked = !!benefit.pending || benefit.from > to;

      return { ...benefit, isLocked, isNew };
    });
  });

  const viewXpRange = computed(
    () =>
      `${compactXp(viewBounds.value.entry)} — ${compactXp(viewBounds.value.exit)} XP`,
  );

  return {
    levelXpTable,

    xp,
    nextLevelXp,
    // O par do contador da faixa do header do jogo. Sai daqui, e não da conta
    // feita em cada tela, porque tem que vir da MESMA leitura que desenha a
    // barra — senão a porcentagem e os números contam histórias diferentes.
    levelXp,
    levelXpSpan,
    level,
    isMaxLevel,
    isUnranked,
    tier,
    tierIndex,
    highlightTierIndex,
    levelProgress,
    xpToNextLevel,
    currentLevelLabel,
    nextLevelLabel,
    playerName,
    playerAvatar,
    openChangeNickname,

    viewSteps,
    playerStepIndex,
    viewIndex,
    viewTierIndex,
    viewTier,
    viewState,
    prevTier,
    nextTier,
    goToStep,
    goToTier,
    stepTier,

    viewBenefits,
    viewXpRange,
  };
}
